// Estado de la oficina v2 en el navegador. Se hidrata con GET /api/oficina/estado al abrir y al
// reconectar el socket, y después vive de los eventos oficina:* (diseño D8). Sin sondeos.
import { create } from 'zustand';
import { useAppStore } from '../../stores/app.store';
import { useToastStore } from '../../stores/toast.store';
import { api } from '../../lib/api';
import { t } from '../../i18n';
import { mensajeError } from './errores';
import { primeraMesaLibre } from './compartido/mesas';
import { oficinaApi, type NuevoGrupo, type OrdenMesas } from './api';
import type { Yo } from './marco/bandejas';
import type { ContratadoDTO, EstadoOficinaDTO, ExpedienteDTO, GrupoDTO, SalaDTO } from './compartido/vocabulario';

/** 04-11 — el filtro «Ver» de la lista: un grupo de una sala, o su «Sin grupo» (grupoId null). null = Todos. */
export interface FiltroGrupo {
  projectId: string;
  grupoId: string | null;
  /** Su nombre al elegirlo (para decir «El grupo Precios se ha quedado vacío» cuando ya no está). */
  nombre: string;
}

interface OficinaState {
  salas: SalaDTO[];
  contratados: Record<string, ContratadoDTO>;
  /** 04-11 — los grupos de mesas de las salas (con gente o vaciados hace < 7 días). Quién está en cuál: `contratado.grupoId`. */
  grupos: GrupoDTO[];
  /** 04-11 — el filtro «Ver» de la lista (null = Todos). La oficina no se filtra nunca: solo la lista. */
  filtroGrupo: FiltroGrupo | null;
  setFiltroGrupo: (f: FiltroGrupo | null) => void;
  /** Crear un grupo (y meter a los elegidos, todo o nada). Devuelve su id cuando la oficina ya lo enseña. */
  crearGrupo: (projectId: string, g: NuevoGrupo) => Promise<string>;
  editarGrupo: (grupoId: string, cambios: { nombre?: string; orden?: number; esperaTemaId?: number | null }) => Promise<void>;
  /** Deshacer un grupo: sus agentes pasan a «Sin grupo» (nunca se cierra a nadie) y se puede volver atrás 6 s. */
  deshacerGrupo: (grupoId: string, conDeshacer?: boolean) => Promise<void>;
  /** 04-11 (#269) — el nombre que propone la recepción para un grupo recién creado; null si no contesta. Nunca falla. */
  nombreSugeridoGrupo: (grupoId: string) => Promise<string | null>;
  /** El grupo en el que contratar (el «+» de su cartel o la lista filtrada por él); null = «Sin grupo». */
  grupoParaContratar: string | null;
  hidratada: boolean;
  /** true mientras la oficina v2 está en pantalla: solo entonces se rehidrata al reconectar. */
  abierta: boolean;
  error: string | null;
  setAbierta: (abierta: boolean) => void;
  /** Ventana de contratar (diseño §10). `salaParaContratar` = sala ya elegida (mesa fantasma). */
  contratarAbierto: boolean;
  salaParaContratar: string | null;
  /** Encargo ya escrito (p. ej. «segunda opinión» de un despliegue): Pau lo lee, lo ajusta y contrata. */
  encargoParaContratar: string | null;
  abrirContratar: (salaId?: string, encargo?: string, grupoId?: string) => void;
  cerrarContratar: () => void;
  hidratar: () => Promise<void>;
  aplicarEstado: (estado: EstadoOficinaDTO) => void;
  actualizarContratado: (c: ContratadoDTO) => void;
  /**
   * Mover a un agente de mesa (09-10; las mesas son automáticas desde el 04-11). Se ve al instante (si la mesa está ocupada, se cambian el
   * sitio); después manda el servidor: se rehidrata siempre, también si falla (y el error sube a quien llama).
   */
  ordenarMesas: (projectId: string, orden: OrdenMesas) => Promise<void>;
  /** Expediente del chat abierto; se recarga con cada aviso del socket sobre ese agente. */
  expedientes: Record<string, ExpedienteDTO>;
  /** Agente cuyo chat está en pantalla (null = ninguno). Al cambiar, el anterior se olvida. */
  chatAbierto: string | null;
  /** Cómo estaba el chat abierto al abrirlo (leído o no): la lista lo ordena así mientras siga abierto (marco/useEquipo). */
  noLeidoAlAbrir: { agentId: string; noLeido: boolean } | null;
  /** 09-10 — «Terminar este trabajo» desde el menú del agente en la escena: el chat se abre con el diálogo de cierre. */
  cierrePedido: string | null;
  /** 10-10 — «Cambiar el nombre» del menú ⋮ del chat: la ficha (Informacion) abre su campo y lo limpia. */
  renombrarPedido: string | null;
  pedirRenombrar: (agentId: string | null) => void;
  pedirCierre: (agentId: string | null) => void;
  /**
   * 04-11 (Entrega 3, Pau aprobó «Aparcar») — aparcar (`true`) o despertar (`false`) a mano. Se ve al instante (se apunta la
   * intención con la hora de ahora; si duerme lo calcula marco/situacion.ts), avisa con «Deshacer» y, si el servidor no
   * lo acepta, vuelve atrás y lo dice. Lo llaman la fila de la lista, el ⋮ del chat y, cuando lo cablee, el menú de la mesa.
   */
  aparcar: (agentId: string, aparcar: boolean, conDeshacer?: boolean) => Promise<void>;
  abrirChat: (agentId: string | null) => void;
  cargarExpediente: (agentId: string) => Promise<void>;
  /** 06-10 — quién mira (su bandeja es la suya); null hasta saberlo (se ve la de Pau, como antes). */
  yo: Yo | null;
  /** La bandeja de otro que está mirando con el selector; null = la suya. */
  bandejaVista: string | null;
  verBandeja: (clave: string | null) => void;
  /** S13 — la lista del equipo plegada a una columna de caras (por defecto en pantallas estrechas; se recuerda). */
  panelPlegado: boolean;
  plegarPanel: (plegado: boolean) => void;
  ajustarPlegadoAlAncho: (ancho: number) => void;
  /** Pau la plegó o desplegó en esta visita (aunque el navegador no deje guardarlo). */
  plegadoAMano: boolean;
  /**
   * «Ordenar» (orden.md regla 16): uno para toda la oficina. Lo enciende y lo apaga su botón (en la franja de los widgets
   * desde el 05-11, #278: `marco/Escenario.tsx`), el menú de un agente, «Listo» y Escape (`mundo/Mundo.tsx`). Única fuente.
   */
  ordenandoMesas: boolean;
  setOrdenandoMesas: (ordenando: boolean | ((antes: boolean) => boolean)) => void;
}

const CLAVE_PLEGADO = 'oficina-panel-plegado';
/** Por debajo de 1024 px (iPad en vertical) la lista abierta deja la oficina en una tira: plegada por defecto.
 *  En iPad en horizontal (1180) se queda abierta: plegada esconde el resumen y el texto de cada fila. */
export const ANCHO_PLEGADO_PX = 1024;
function preferenciaPlegado(): boolean | null {
  try {
    const guardado = localStorage.getItem(CLAVE_PLEGADO);
    return guardado === null ? null : guardado === '1';
  } catch {
    return null;
  }
}
function plegadoInicial(): boolean {
  if (typeof window === 'undefined') return false;
  return preferenciaPlegado() ?? window.innerWidth < ANCHO_PLEGADO_PX;
}

// Número de la última carga pedida por agente, de un contador GLOBAL que nunca se reinicia: una
// respuesta más vieja que llegue tarde no pisa a una más nueva (tampoco yendo y volviendo A → B → A).
let secuencia = 0;
const cargas = new Map<string, number>();

/** Sube con cada cambio de mesas de Pau: `hidratar` descarta las fotos pedidas antes. */
let generacionMesas = 0;
/** Cambios de mesas aún sin respuesta del servidor: mientras haya alguno, ninguna foto ni aviso pisa la mesa que ve Pau
 *  (dos movimientos seguidos: la foto de tras el primero devolvía al agente a la mesa del primero). */
let mesasEnVuelo = 0;

/**
 * 09-10 (Pau aprobó la maqueta «deshacer al cambiar de sitio»): qué decir tras mover a un agente y la orden que lo
 * deshace (volver a su mesa de antes; si se cambió el sitio con otro, el servidor vuelve a cambiarlos).
 * Se calcula con lo que se veía ANTES de mover. undefined si no es un movimiento real.
 */
export function deshacerMover(
  contratados: Record<string, ContratadoDTO>,
  projectId: string,
  orden: OrdenMesas,
  nombre: (c: ContratadoDTO) => string,
  nombreGrupo: (grupoId: string | null) => string = () => '',
): { mensaje: string; deshacer: OrdenMesas } | undefined {
  const destino = destinoDe(contratados, projectId, orden);
  if (!destino) return undefined;
  const { yo, grupoId, mesa, otro } = destino;
  const antes = yo.grupoId ?? null;
  if (antes === grupoId && yo.mesa === mesa) return undefined;
  const mensaje = otro
    ? t('oficinaV2.mesas.cambiados', { a: nombre(yo), b: nombre(otro) })
    : antes !== grupoId
      ? t(grupoId ? 'oficinaV2.grupos.movidoA' : 'oficinaV2.grupos.movidoFuera', { nombre: nombre(yo), grupo: nombreGrupo(grupoId) })
      : t('oficinaV2.mesas.movido', { nombre: nombre(yo), n: mesa + 1 });
  return { mensaje, deshacer: { accion: 'mover', agentId: yo.agentId, grupoId: antes, mesa: yo.mesa } };
}

/**
 * 04-11 — adónde va de verdad un movimiento (lo mismo que decide el servidor, backend/src/oficina/salas.ts): el grupo
 * (ausente = el suyo), la mesa (ausente = la primera libre de ese grupo) y con quién se cambia el sitio si está ocupada.
 */
export function destinoDe(
  contratados: Record<string, ContratadoDTO>,
  projectId: string,
  orden: OrdenMesas,
): { yo: ContratadoDTO; grupoId: string | null; mesa: number; otro: ContratadoDTO | undefined } | undefined {
  const yo = contratados[orden.agentId];
  if (!yo || yo.projectId !== projectId) return undefined;
  const grupoId = orden.grupoId === undefined ? (yo.grupoId ?? null) : orden.grupoId;
  const enDestino = Object.values(contratados).filter((c) => c.projectId === projectId && (c.grupoId ?? null) === grupoId && c.agentId !== yo.agentId);
  const mismoGrupo = (yo.grupoId ?? null) === grupoId;
  const mesa = orden.mesa ?? (mismoGrupo ? yo.mesa : primeraMesaLibre(enDestino.map((c) => c.mesa)));
  return { yo, grupoId, mesa, otro: enDestino.find((c) => c.mesa === mesa) };
}

export const useOficinaStore = create<OficinaState>((set, get) => ({
  salas: [],
  contratados: {},
  hidratada: false,
  abierta: false,
  error: null,
  setAbierta: (abierta) => set({ abierta }),
  contratarAbierto: false,
  salaParaContratar: null,
  encargoParaContratar: null,
  abrirContratar: (salaId, encargo, grupoId) => {
    // Con la lista filtrada por un grupo, contratar va a ese grupo (grupos.md regla 8), salvo que se pida otra sala.
    const f = get().filtroGrupo;
    const grupo = grupoId ?? (f?.grupoId && (!salaId || salaId === f.projectId) ? f.grupoId : null);
    const sala = salaId ?? (grupo ? get().grupos.find((g) => g.id === grupo)?.projectId : undefined);
    set({ contratarAbierto: true, salaParaContratar: sala ?? null, encargoParaContratar: encargo ?? null, grupoParaContratar: grupo });
  },
  cerrarContratar: () => set({ contratarAbierto: false, salaParaContratar: null, encargoParaContratar: null, grupoParaContratar: null }),
  grupos: [],
  filtroGrupo: null,
  setFiltroGrupo: (filtroGrupo) => set({ filtroGrupo }),
  grupoParaContratar: null,
  crearGrupo: async (projectId, g) => {
    const { id } = await oficinaApi.crearGrupo(projectId, g);
    await get().hidratar();
    return id;
  },
  editarGrupo: async (grupoId, cambios) => {
    // Al instante (nombre, orden, contrato): si el servidor no lo acepta, la recarga lo devuelve como estaba.
    set((s) => ({ grupos: s.grupos.map((g) => (g.id === grupoId ? { ...g, ...cambios } : g)) }));
    try {
      await oficinaApi.editarGrupo(grupoId, cambios);
    } finally {
      void get().hidratar();
    }
  },
  nombreSugeridoGrupo: (grupoId) =>
    oficinaApi
      .nombreSugeridoGrupo(grupoId)
      .then((r) => (typeof r.nombre === 'string' && r.nombre.trim() ? r.nombre.trim() : null))
      .catch(() => null),
  deshacerGrupo: async (grupoId, conDeshacer = true) => {
    const { deshecho } = await oficinaApi.deshacerGrupo(grupoId);
    if (get().filtroGrupo?.grupoId === grupoId) set({ filtroGrupo: null });
    await get().hidratar();
    if (!conDeshacer) return;
    useToastStore.getState().push({
      message: t('oficinaV2.grupos.deshechoAviso', { nombre: deshecho.nombre, n: deshecho.agentIds.length }),
      durationMs: 6_000,
      action: {
        label: t('oficinaV2.mesas.deshacer'),
        onClick: () =>
          void get()
            .crearGrupo(deshecho.projectId, { nombre: deshecho.nombre, agentIds: deshecho.agentIds, esperaTemaId: deshecho.esperaTemaId, orden: deshecho.orden })
            .catch((e: unknown) => useToastStore.getState().push({ message: mensajeError(e) })),
      },
    });
  },
  yo: null,
  bandejaVista: null,
  verBandeja: (clave) => set({ bandejaVista: clave }),
  panelPlegado: plegadoInicial(),
  plegadoAMano: false,
  ordenandoMesas: false,
  setOrdenandoMesas: (ordenando) => set((s) => ({ ordenandoMesas: typeof ordenando === 'function' ? ordenando(s.ordenandoMesas) : ordenando })),
  plegarPanel: (plegado) => {
    try {
      localStorage.setItem(CLAVE_PLEGADO, plegado ? '1' : '0');
    } catch {
      /* sin almacenamiento (modo privado): vale para esta visita (plegadoAMano) */
    }
    // Plegada no hay selector de bandejas: se vuelve a la propia para no ver las caras de otro sin saberlo.
    set(plegado ? { panelPlegado: true, plegadoAMano: true, bandejaVista: null } : { panelPlegado: false, plegadoAMano: true });
  },
  ajustarPlegadoAlAncho: (ancho) => {
    // Solo si Pau no lo ha elegido a mano: girar el iPad pliega o despliega sola la lista.
    if (get().plegadoAMano || preferenciaPlegado() !== null) return;
    const plegado = ancho < ANCHO_PLEGADO_PX;
    if (plegado !== get().panelPlegado) set(plegado ? { panelPlegado: true, bandejaVista: null } : { panelPlegado: false });
  },
  hidratar: async () => {
    // Quién mira, para enseñarle su bandeja. Si falla, se queda la de Pau (lo de siempre) y se reintenta al rehidratar.
    api<{ usuario?: Yo | null }>('/api/auth/me')
      .then((r) => set({ yo: r.usuario ? { rol: r.usuario.rol, cuentaId: r.usuario.cuentaId ?? null } : null }))
      .catch(() => undefined);
    // Revisión 09-10: una foto pedida ANTES de que Pau ordenara las mesas no pisa lo que ya ve (el agente que acaba de
    // mover volvía un instante a su silla vieja). Se descarta; la que se pide después del cambio llega detrás.
    const desde = generacionMesas;
    try {
      const estado = await oficinaApi.estado();
      if (desde !== generacionMesas || mesasEnVuelo > 0) return;
      useOficinaStore.getState().aplicarEstado(estado);
    } catch {
      set({ error: 'carga' });
    }
  },
  aplicarEstado: (estado) =>
    set({
      salas: estado.salas,
      contratados: Object.fromEntries(estado.contratados.map((c) => [c.agentId, c])),
      grupos: estado.grupos ?? [],
      hidratada: true,
      error: null,
    }),
  actualizarContratado: (c) =>
    set((s) => {
      const siguen = c.ciclo !== 'cerrado' && c.ciclo !== 'despedido';
      const contratados = { ...s.contratados };
      const antes = s.contratados[c.agentId];
      if (siguen) contratados[c.agentId] = mesasEnVuelo > 0 && antes ? { ...c, mesa: antes.mesa, grupoId: antes.grupoId ?? null } : c;
      else delete contratados[c.agentId];
      return { contratados };
    }),
  ordenarMesas: async (projectId, orden) => {
    generacionMesas += 1;
    mesasEnVuelo += 1;
    set((s) => {
      const destino = destinoDe(s.contratados, projectId, orden);
      if (!destino) return {};
      const { yo, grupoId, mesa, otro } = destino;
      const contratados = { ...s.contratados, [yo.agentId]: { ...yo, grupoId, mesa } };
      if (otro) contratados[otro.agentId] = { ...otro, grupoId: yo.grupoId ?? null, mesa: yo.mesa };
      return { contratados };
    });
    try {
      await oficinaApi.ordenarMesas(projectId, orden);
    } finally {
      generacionMesas += 1;
      mesasEnVuelo -= 1;
      // Solo el último en volver recarga: la foto de los anteriores aún no lleva los que siguen en vuelo.
      if (mesasEnVuelo === 0) void get().hidratar();
    }
  },
  expedientes: {},
  chatAbierto: null,
  noLeidoAlAbrir: null,
  cierrePedido: null,
  renombrarPedido: null,
  pedirRenombrar: (agentId) => set({ renombrarPedido: agentId }),
  pedirCierre: (agentId) => set({ cierrePedido: agentId }),
  aparcar: async (agentId, aparcar, conDeshacer = true) => {
    const antes = get().contratados[agentId];
    if (!antes) return;
    const campo = aparcar ? 'aparcadoEn' : 'despertadoEn';
    set((s) => (s.contratados[agentId] ? { contratados: { ...s.contratados, [agentId]: { ...s.contratados[agentId], [campo]: new Date().toISOString() } } } : {}));
    try {
      await oficinaApi.aparcar(agentId, aparcar);
    } catch (e) {
      set((s) => (s.contratados[agentId] ? { contratados: { ...s.contratados, [agentId]: { ...s.contratados[agentId], [campo]: antes[campo] } } } : {}));
      useToastStore.getState().push({ message: t('oficinaV2.lista.aparcado.error', { motivo: mensajeError(e) }) });
      return;
    }
    const nombre = antes.tema?.trim() || t('oficinaV2.sinTitulo');
    useToastStore.getState().push({
      message: t(aparcar ? 'oficinaV2.lista.aparcado.aparcadoAviso' : 'oficinaV2.lista.aparcado.despertadoAviso', { nombre }),
      durationMs: 6_000,
      ...(conDeshacer ? { action: { label: t('oficinaV2.lista.aparcado.deshacer'), onClick: () => void get().aparcar(agentId, !aparcar, false) } } : {}),
    });
  },
  abrirChat: (agentId) => {
    if (get().chatAbierto === agentId) return;
    set((s) => {
      const expedientes = { ...s.expedientes };
      if (s.chatAbierto) {
        delete expedientes[s.chatAbierto];
      }
      const c = agentId ? s.contratados[agentId] : undefined;
      return { chatAbierto: agentId, expedientes, noLeidoAlAbrir: agentId ? { agentId, noLeido: !!c?.noLeido } : null };
    });
    // Al abrir se carga YA lo que tiene pendiente (propuesta, pregunta, permisos). Antes solo se cargaba con
    // el siguiente aviso del socket: con el agente quieto esperando a Pau, las tarjetas no salían nunca
    // (29-09: «no tengo el botón para aceptar el permiso»). Lo cargaba el Expediente viejo, retirado en S10.
    if (agentId) void get().cargarExpediente(agentId);
  },
  cargarExpediente: async (agentId) => {
    const n = ++secuencia;
    cargas.set(agentId, n);
    try {
      const e = await oficinaApi.expediente(agentId);
      if (cargas.get(agentId) !== n || useOficinaStore.getState().chatAbierto !== agentId) return;
      set((s) => ({ expedientes: { ...s.expedientes, [agentId]: e } }));
    } catch {
      // Se reintenta con el siguiente aviso del socket sobre este agente (el chat sigue abierto).
    }
  },
}));

// Varios avisos seguidos de un mismo agente (parte + historial) → una sola recarga.
const recargas = new Map<string, ReturnType<typeof setTimeout>>();
export function recargarExpedienteSiAbierto(agentId: string): void {
  if (useOficinaStore.getState().chatAbierto !== agentId) return;
  clearTimeout(recargas.get(agentId));
  recargas.set(
    agentId,
    setTimeout(() => {
      recargas.delete(agentId);
      void useOficinaStore.getState().cargarExpediente(agentId);
    }, 250),
  );
}

/**
 * Al (re)conectar el socket (29-09): lo que pasó mientras estaba caído (mensajes, un permiso nuevo, una
 * respuesta) no llegó por ningún aviso, así que el chat abierto se quedaba viejo hasta cerrarlo y abrirlo.
 * Se recargan su conversación y su expediente (las tarjetas de la franja salen de ahí).
 */
export function recargarChatAbierto(): void {
  const agentId = useOficinaStore.getState().chatAbierto;
  if (!agentId) return;
  void useOficinaStore.getState().cargarExpediente(agentId);
  // Si falla, se queda lo que había; el siguiente aviso del socket lo pone al día.
  useAppStore.getState().loadMessages(agentId).catch(() => undefined);
}
